import React, { useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { useSourceSearch } from "features/SourceSearch";
import { getAdapter } from "features/CategoryNews/adapters";

// Unified infinite scroller that drives data loading based on category type
// Expects parent to control offset via IntersectionObserver and pass setters

const InfiniteScroller = ({
  categoryType,
  currentCategory,
  offset,
  setLoading,
  setcategoryData,
  setoffset,
  setHasMore,
}) => {
  const params = useParams();
  const { user } = useSelector((state) => state.auth);

  const {
    currentSearchTerm,
    currentTimeframe,
    startDate,
    endDate,
    sourceCategories,
    currentSource,
    currentSubCategory,
    sources,
    handleSourceCategoryChange,
  } = useSourceSearch();

  // Keep page counter when some strategies use page-based (boclips), others offset-based
  const pageRef = useRef(0);
  const resetSeqRef = useRef(0);

  const getAdapterForType = () => {
    const type = currentCategory === "boclips" ? "boclips" : categoryType;
    return getAdapter({ type, searchContext: {
      currentSearchTerm,
      currentTimeframe,
      startDate,
      endDate,
      sourceCategories,
      currentCategory,
      currentSource,
      currentSubCategory,
      sources,
      handleSourceCategoryChange,
    }, params, user });
  };

  const load = async (cursorOrPage, clear = false, seq) => {
    const adapter = getAdapterForType();
    const { idKey, isPageMode } = adapter;
    setLoading(true);
    try {
      let result;
      result = await adapter.fetchPage(cursorOrPage);

      const { items, nextCursor, hasMore } = result;
      if (seq != null && seq !== resetSeqRef.current) {
        return { next: nextCursor };
      }
      setcategoryData(items, idKey, clear);
      setHasMore(hasMore);
      setLoading(false);
      return { next: nextCursor };
    } catch (e) {
      console.error(e);
      setHasMore(false);
      setLoading(false);
    }
  };

  // Initial load/reset on key dependency changes
  useEffect(() => {
    const resetAndLoad = async () => {
      const adapter = getAdapterForType();
      const { idKey, isPageMode, initialCursor } = adapter;
      const seq = ++resetSeqRef.current;
      setLoading(true);
      setcategoryData([], idKey, true);
      // Reset offset/page
      setoffset(0);
      pageRef.current = isPageMode ? initialCursor : 0;
      // initial load
      const next = await load(pageRef.current === 0 ? 0 : pageRef.current, false, seq);
      pageRef.current = next?.next ?? pageRef.current;
    };

    resetAndLoad();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentSearchTerm,
    currentTimeframe,
    startDate,
    endDate,
    currentCategory,
    currentSource,
    currentSubCategory,
    params.source,
    params.category,
    params.search,
  ]);

  // Subsequent loads on offset increments (for offset-based only)
  useEffect(() => {
    const fetchMore = async () => {
      const adapter = getAdapterForType();
      const seq = resetSeqRef.current;
      if (adapter.isPageMode) {
        const next = await load(pageRef.current, false, seq);
        pageRef.current = next?.next ?? pageRef.current + 1;
        return;
      }
      if (offset !== 0) {
        const next = await load(offset, false, seq);
        pageRef.current = next?.next ?? offset;
      }
    };
    if (offset !== 0) fetchMore();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offset]);

  return null;
};

export default InfiniteScroller;


